
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Button, Grid, Chip, LinearProgress,
  AppBar, Toolbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Tooltip
} from '@mui/material';
import {
  Gavel, HowToVote, Add, Refresh, AccountBalance,
  Timeline, CheckCircle, Cancel, Remove, Info, Description,
  TrendingUp, People, LocalFireDepartment
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import strictMonochromeTheme from '../utils/strictMonochromeTheme';
import { ethers } from 'ethers';

const AVO_NETWORKS = {
  devnet: { rpcUrl: 'http://localhost:9545', chainId: 1337 },
  testnet: { rpcUrl: 'http://localhost:9546', chainId: 1338 },
  mainnet: { rpcUrl: 'http://localhost:9547', chainId: 1339 }
};

const Governance = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [voteDialog, setVoteDialog] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [governanceStats, setGovernanceStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newProposal, setNewProposal] = useState({
    title: '', description: '', category: 'protocol',
    parameter: '', currentValue: '', newValue: ''
  });

  const getProviderAndAddress = async () => {
    const network = localStorage.getItem('network') || 'devnet';
    const networkConfig = AVO_NETWORKS[network];
    const provider = new ethers.providers.JsonRpcProvider(networkConfig.rpcUrl, {
      name: network, chainId: networkConfig.chainId
    });
    const privateKey = localStorage.getItem('privateKey');
    if (!privateKey) throw new Error('No wallet found');
    const wallet = new ethers.Wallet(privateKey, provider);
    return { provider, address: wallet.address };
  };

  const callRpcMethod = async (method, params = []) => {
    const network = localStorage.getItem('network') || 'devnet';
    const networkConfig = AVO_NETWORKS[network];
    const response = await fetch(networkConfig.rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: method, params: params })
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.error) throw new Error(data.error.message || 'RPC error');
    return data.result;
  };

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const privateKey = localStorage.getItem('privateKey');
      if (!privateKey) {
        navigate('/login');
        return;
      }
      
      console.log('🔍 Fetching proposals from AVO RPC...');
      const proposalsList = await callRpcMethod('avo_listProposals', []);
      console.log('📋 Proposals list received:', proposalsList);
      
      if (!proposalsList || proposalsList.length === 0) {
        console.log('⚠️ No proposals found in network');
        setProposals([]);
        const stats = await callRpcMethod('avo_getGovernanceStats', []);
        setGovernanceStats({
          totalVotes: stats.total_votes_cast || 0,
          totalBurned: stats.total_fees_burned ? parseInt(stats.total_fees_burned, 16) / 1e18 : 0,
          voteFee: stats.vote_fee ? parseInt(stats.vote_fee, 16) / 1e18 : 1,
          adminAddress: stats.admin_address,
          phase: stats.phase
        });
        setLoading(false);
        return;
      }
      
      console.log(`✅ Found ${proposalsList.length} proposal(s), fetching details...`);
      const detailedProposals = await Promise.all(proposalsList.map(async (prop) => {
        const details = await callRpcMethod('avo_getProposal', [{ proposal_id: prop.id }]);
        console.log('📄 Proposal details:', details);
        const forVotes = details.vote_counts?.for ? parseInt(details.vote_counts.for, 16) : 0;
        const againstVotes = details.vote_counts?.against ? parseInt(details.vote_counts.against, 16) : 0;
        const abstainVotes = details.vote_counts?.abstain ? parseInt(details.vote_counts.abstain, 16) : 0;
        return {
          id: details.id,
          title: details.title,
          description: details.description,
          proposer: details.proposer,
          status: details.status?.toLowerCase() || 'active',
          category: details.proposal_type || 'custom',
          votingEnds: details.voting_ends_at * 1000,
          forVotes: forVotes / 1e18,
          againstVotes: againstVotes / 1e18,
          abstainVotes: abstainVotes / 1e18,
          parameter: details.parameter,
          currentValue: details.current_value,
          newValue: details.new_value,
          createdAt: details.created_at * 1000
        };
      }));
      
      console.log(`✅ Loaded ${detailedProposals.length} proposal(s) with full details`);
      setProposals(detailedProposals);
      
      const stats = await callRpcMethod('avo_getGovernanceStats', []);
      console.log('📊 Governance stats:', stats);
      setGovernanceStats({
        totalVotes: stats.total_votes_cast || 0,
        totalBurned: stats.total_fees_burned ? parseInt(stats.total_fees_burned, 16) / 1e18 : 0,
        voteFee: stats.vote_fee ? parseInt(stats.vote_fee, 16) / 1e18 : 1,
        adminAddress: stats.admin_address,
        phase: stats.phase
      });
    } catch (error) {
      console.error('❌ Error fetching proposals:', error);
      setError(`Failed to load proposals: ${error.message}`);
    }
    setLoading(false);
  }, [navigate]); // useCallback dependencies

  useEffect(() => {
    // Fetch proposals immediately on mount
    fetchProposals();
    
    // Setup auto-refresh every 5 seconds to detect new proposals in real-time
    const intervalId = setInterval(() => {
      fetchProposals();
    }, 5000);
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [fetchProposals]); // Add fetchProposals as dependency


  const getVotingProgress = (proposal) => proposal.forVotes + proposal.againstVotes + proposal.abstainVotes;

  const handleCreateProposal = async () => {
    setError(''); setSuccess('');
    try {
      const { address } = await getProviderAndAddress();
      const result = await callRpcMethod('avo_submitProposal', [{
        proposer: address,
        proposal_type: newProposal.category,
        title: newProposal.title,
        description: newProposal.description,
        parameter: newProposal.parameter || null,
        current_value: newProposal.currentValue || null,
        new_value: newProposal.newValue || null
      }]);
      setSuccess(`Proposal ${result.proposal_id} created successfully!`);
      setCreateDialog(false);
      setNewProposal({ title: '', description: '', category: 'protocol', parameter: '', currentValue: '', newValue: '' });
      await fetchProposals();
    } catch (error) {
      console.error('Error creating proposal:', error);
      setError(error.message || 'Failed to create proposal');
    }
  };

  const handleVote = async (proposal, voteType) => {
    setError(''); setSuccess('');
    try {
      const { address } = await getProviderAndAddress();
      const result = await callRpcMethod('avo_castVote', [{
        voter: address,
        proposal_id: proposal.id,
        choice: voteType.toLowerCase()
      }]);
      const feeBurned = result.fee_burned ? parseInt(result.fee_burned, 16) / 1e18 : 1;
      setSuccess(`Vote cast successfully! ${feeBurned} AVO burned as fee.`);
      setVoteDialog(false);
      setSelectedProposal(null);
      await fetchProposals();
    } catch (error) {
      console.error('Error voting:', error);
      setError(error.message || 'Failed to cast vote');
    }
  };

  return (
    <ThemeProvider theme={strictMonochromeTheme}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#000000', color: '#FFFFFF' }}>
        <AppBar position="static" sx={{ backgroundColor: '#000000', borderBottom: '1px solid #FFFFFF' }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, letterSpacing: '2px' }}>
              AVO_STATION
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={() => navigate('/dashboard')} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000' } }}>
                DASHBOARD
              </Button>
              <Button variant="outlined" onClick={fetchProposals} startIcon={<Refresh sx={{ color: '#FFFFFF' }} />} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000', '& .MuiSvgIcon-root': { color: '#000000' } } }}>
                REFRESH
              </Button>
              <Button variant="outlined" startIcon={<Add sx={{ color: '#FFFFFF' }} />} onClick={() => setCreateDialog(true)} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000', '& .MuiSvgIcon-root': { color: '#000000' } } }}>
                CREATE PROPOSAL
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, backgroundColor: '#000000', border: '1px solid #FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, backgroundColor: '#000000', border: '1px solid #FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <Description sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>{proposals.length}</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>TOTAL PROPOSALS</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <HowToVote sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>{proposals.filter(p => p.status === 'active').length}</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>ACTIVE VOTING</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <TrendingUp sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>{governanceStats?.totalVotes || 0}</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>VOTES CAST</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
                <LocalFireDepartment sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FFFFFF' }}>{governanceStats?.totalBurned?.toFixed(2) || '0.00'}</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1, letterSpacing: '1px' }}>AVO BURNED</Typography>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #FFFFFF' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '2px', color: '#FFFFFF' }}>GOVERNANCE PROPOSALS</Typography>
            </Box>
            
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#FFFFFF' }} />
                <Typography sx={{ mt: 2, color: '#FFFFFF' }}>Loading proposals from AVO network...</Typography>
              </Box>
            ) : proposals.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Gavel sx={{ fontSize: 60, color: '#FFFFFF', mb: 2 }} />
                <Typography sx={{ color: '#FFFFFF', mb: 1 }}>No proposals yet</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>Be the first to create a governance proposal</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: '1px solid #FFFFFF' }}>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>PROPOSAL</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>STATUS</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>VOTING</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>ENDS</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, borderBottom: '1px solid #FFFFFF' }}>ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {proposals.map((proposal) => (
                      <TableRow key={proposal.id} sx={{ '&:hover': { backgroundColor: '#1A1A1A' }, borderBottom: '1px solid #FFFFFF' }}>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: '#FFFFFF' }}>{proposal.title}</Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF', maxWidth: 400, mb: 1 }}>{proposal.description.slice(0, 120)}...</Typography>
                            <Stack direction="row" spacing={1}>
                              <Chip label={proposal.category.toUpperCase()} size="small" sx={{ backgroundColor: '#000000', color: '#FFFFFF', border: '1px solid #FFFFFF', borderRadius: 0 }} />
                              <Chip label={`BY: ${proposal.proposer.slice(0, 8)}...`} size="small" sx={{ backgroundColor: '#000000', color: '#FFFFFF', border: '1px solid #FFFFFF', borderRadius: 0 }} />
                            </Stack>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Chip label={proposal.status.toUpperCase()} sx={{ backgroundColor: '#000000', color: '#FFFFFF', border: '1px solid #FFFFFF', fontWeight: 600, borderRadius: 0 }} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Box sx={{ width: '100%', maxWidth: 250 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ color: '#FFFFFF' }}>FOR: {proposal.forVotes.toFixed(2)} AVO</Typography>
                                <LinearProgress variant="determinate" value={(proposal.forVotes / (getVotingProgress(proposal) || 1)) * 100} sx={{ height: 6, backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0, '& .MuiLinearProgress-bar': { backgroundColor: '#FFFFFF' } }} />
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ color: '#FFFFFF' }}>AGAINST: {proposal.againstVotes.toFixed(2)} AVO</Typography>
                                <LinearProgress variant="determinate" value={(proposal.againstVotes / (getVotingProgress(proposal) || 1)) * 100} sx={{ height: 6, backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0, '& .MuiLinearProgress-bar': { backgroundColor: '#FFFFFF' } }} />
                              </Box>
                            </Stack>
                            <Typography variant="caption" sx={{ color: '#FFFFFF', display: 'block', mt: 1 }}>Total: {getVotingProgress(proposal).toFixed(2)} AVO</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>{proposal.status === 'active' ? new Date(proposal.votingEnds).toLocaleDateString() : 'Ended'}</Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #FFFFFF' }}>
                          {proposal.status === 'active' && (
                            <Button size="small" variant="outlined" startIcon={<HowToVote sx={{ color: '#FFFFFF' }} />} onClick={() => { setSelectedProposal(proposal); setVoteDialog(true); }} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000', '& .MuiSvgIcon-root': { color: '#000000' } } }}>
                              VOTE
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Box>

        <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 } }}>
          <DialogTitle sx={{ borderBottom: '1px solid #FFFFFF', color: '#FFFFFF', fontWeight: 600, letterSpacing: '1px' }}>CREATE NEW PROPOSAL</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Proposal Title" value={newProposal.title} onChange={(e) => setNewProposal({...newProposal, title: e.target.value})} variant="outlined" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: '#FFFFFF' } }, '& .MuiInputLabel-root': { color: '#FFFFFF' }, '& .MuiInputBase-input': { color: '#FFFFFF' } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" multiline rows={4} value={newProposal.description} onChange={(e) => setNewProposal({...newProposal, description: e.target.value})} variant="outlined" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: '#FFFFFF' } }, '& .MuiInputLabel-root': { color: '#FFFFFF' }, '& .MuiInputBase-input': { color: '#FFFFFF' } }} />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#FFFFFF' }}>Category</InputLabel>
                  <Select value={newProposal.category} onChange={(e) => setNewProposal({...newProposal, category: e.target.value})} label="Category" sx={{ borderRadius: 0, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' }, '& .MuiSelect-select': { color: '#FFFFFF' }, '& .MuiSvgIcon-root': { color: '#FFFFFF' } }}>
                    <MenuItem value="protocol">Protocol</MenuItem>
                    <MenuItem value="governance">Governance</MenuItem>
                    <MenuItem value="treasury">Treasury</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Parameter (optional)" value={newProposal.parameter} onChange={(e) => setNewProposal({...newProposal, parameter: e.target.value})} variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: '#FFFFFF' } }, '& .MuiInputLabel-root': { color: '#FFFFFF' }, '& .MuiInputBase-input': { color: '#FFFFFF' } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="New Value (optional)" value={newProposal.newValue} onChange={(e) => setNewProposal({...newProposal, newValue: e.target.value})} variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: '#FFFFFF' } }, '& .MuiInputLabel-root': { color: '#FFFFFF' }, '& .MuiInputBase-input': { color: '#FFFFFF' } }} />
              </Grid>
            </Grid>
            <Alert severity="info" icon={<Info sx={{ color: '#FFFFFF' }} />} sx={{ mt: 2, backgroundColor: '#000000', border: '1px solid #FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>
              {governanceStats?.phase?.AdminControlled ? `Only admin can create proposals during bootstrap phase. Voting fee: ${governanceStats.voteFee} AVO per vote (burned).` : 'Anyone can create proposals. Voting fee will be burned.'}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #FFFFFF', p: 2 }}>
            <Button onClick={() => setCreateDialog(false)} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>CANCEL</Button>
            <Button onClick={handleCreateProposal} variant="outlined" sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000' } }}>CREATE PROPOSAL</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={voteDialog} onClose={() => setVoteDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: '#000000', border: '1px solid #FFFFFF', borderRadius: 0 } }}>
          <DialogTitle sx={{ borderBottom: '1px solid #FFFFFF', color: '#FFFFFF', fontWeight: 600, letterSpacing: '1px' }}>VOTE ON PROPOSAL</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedProposal && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#FFFFFF', fontWeight: 600 }}>{selectedProposal.title}</Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 3 }}>{selectedProposal.description}</Typography>
                <Box sx={{ mb: 3, p: 2, border: '1px solid #FFFFFF' }}>
                  <Typography variant="caption" sx={{ color: '#FFFFFF', mb: 1, display: 'block' }}>Current Votes:</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>FOR: {selectedProposal.forVotes.toFixed(2)} AVO</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>AGAINST: {selectedProposal.againstVotes.toFixed(2)} AVO</Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>ABSTAIN: {selectedProposal.abstainVotes.toFixed(2)} AVO</Typography>
                </Box>
                <Alert severity="warning" icon={<LocalFireDepartment sx={{ color: '#FFFFFF' }} />} sx={{ mb: 3, backgroundColor: '#000000', border: '1px solid #FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>
                  Voting will burn {governanceStats?.voteFee || 1} AVO as a fee. Your voting power equals your balance.
                </Alert>
                <Stack spacing={2}>
                  <Button fullWidth variant="outlined" startIcon={<CheckCircle sx={{ color: '#FFFFFF' }} />} onClick={() => handleVote(selectedProposal, 'for')} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000', '& .MuiSvgIcon-root': { color: '#000000' } } }}>VOTE FOR</Button>
                  <Button fullWidth variant="outlined" startIcon={<Cancel sx={{ color: '#FFFFFF' }} />} onClick={() => handleVote(selectedProposal, 'against')} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000', '& .MuiSvgIcon-root': { color: '#000000' } } }}>VOTE AGAINST</Button>
                  <Button fullWidth variant="outlined" startIcon={<Remove sx={{ color: '#FFFFFF' }} />} onClick={() => handleVote(selectedProposal, 'abstain')} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0, '&:hover': { backgroundColor: '#FFFFFF', color: '#000000', '& .MuiSvgIcon-root': { color: '#000000' } } }}>ABSTAIN</Button>
                </Stack>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderBottom: '1px solid #FFFFFF', p: 2 }}>
            <Button onClick={() => setVoteDialog(false)} sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}>CANCEL</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default Governance;
